// This strange line is required for TypeScript support https://mui.com/material-ui/customization/theming/#typescript
import { type LinkProps } from '@mui/material';
import type { PaletteColorOptions, Theme } from '@mui/material/styles';
import { alpha, createTheme } from '@mui/material/styles';
import type { AllSystemCSSProperties, ResponsiveStyleValue } from '@mui/system';
import { Link as RouterLink, useSearchParams } from 'react-router';

import { mergeSearchParams } from '~/utils/urlBuilder';

// This is to define the new variants
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    secondary: true;
  }
}

const LinkBehavior = (props: LinkProps & { ref?: React.Ref<HTMLAnchorElement> }) => {
  const [searchParams] = useSearchParams();
  const { href, ref, children, ...rest } = props;

  if (!href || /^[a-z]+:/.test(href)) {
    return (
      <a ref={ref} href={href} {...rest}>
        {children}
      </a>
    );
  }

  const url = new URL(href, window.location.origin);
  const mergedParams = mergeSearchParams({
    currentSearchParams: searchParams,
    newSearchParams: url.searchParams,
  });
  const to = `${url.pathname}?${mergedParams.toString()}`;

  return (
    <RouterLink ref={ref} to={to} {...rest}>
      {children}
    </RouterLink>
  );
};

const { palette } = createTheme();
const createColor = (mainColor: string) => palette.augmentColor({ color: { main: mainColor } });

/**
 * Panels: cards, page bands, table containers, fields. Menus, popovers and dialogs
 * are pinned to `TRANSIENT_SURFACE_RADIUS` instead — 16px reads bubbly on a compact
 * dropdown and eats into its first row.
 */
export const SURFACE_RADIUS = 16;

/** Menus, popovers, dialogs, and blocks nested inside a panel — see `SURFACE_RADIUS`. */
export const TRANSIENT_SURFACE_RADIUS = 8;

/**
 * A button's corner. Pills stay for filters, statuses and tags. Applied per call site
 * rather than as a `MuiButton` default, which would reshape every button app-wide.
 */
export const BUTTON_RADIUS = 10;
export const BUTTON_SX = { borderRadius: `${BUTTON_RADIUS}px` } as const;

/** The app's panel: a bordered surface at the shared radius. */
export const CARD_SX = {
  borderRadius: `${SURFACE_RADIUS}px`,
  border: '1px solid',
  borderColor: 'divider',
} as const;

/**
 * The shape shared by every filter control in the global band and by the search box
 * on each table toolbar. `height` is part of the token because the three
 * implementations this replaced disagreed about it and the row never lined up.
 */
export const FILTER_CONTROL_SX = {
  height: 32,
  borderRadius: '999px',
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  fontSize: 14,
  textTransform: 'none',
  '&:hover': { borderColor: 'text.disabled', bgcolor: 'background.paper' },
} as const;

/** Spread over `FILTER_CONTROL_SX` when the control holds a selection rather than "All". */
export const FILTER_CONTROL_ACTIVE_SX = {
  borderColor: 'primary.main',
  bgcolor: 'primary.light',
  color: 'primary.main',
  '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.light' },
} as const;

/**
 * Spread over `FILTER_CONTROL_SX` for a control the page can't narrow by. Greyed text
 * alone was too quiet — the pills are white on a near-white band.
 */
export const FILTER_CONTROL_DISABLED_SX = {
  bgcolor: 'action.hover',
  borderColor: 'divider',
  color: 'action.disabled',
} as const;

/** Surface for page header bands. Its own token rather than `selectedColor`, which means "selected" on rows everywhere else. */
export const pageBandColor = '#EFF4FB';

/** Rules inside a page band. Lighter than `divider`, and tinted to the band rather than grey. */
export const pageBandDividerColor = 'rgba(27, 86, 187, 0.14)';

declare module '@mui/material/styles' {
  interface CustomPalette {
    lightButton: PaletteColorOptions;
  }
  interface Palette extends CustomPalette {}
  interface PaletteOptions extends CustomPalette {}
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    lightButton: true;
  }
}

const baseTheme = createTheme({
  palette: {
    primary: {
      main: '#1b56bb',
      light: '#e7f2ff',
      dark: '#09295f',
    },
    error: {
      main: '#D32F2F',
      light: '#ffcdd2',
    },
    warning: {
      main: '#ED6C02',
    },
    info: {
      main: '#2f6fd6',
    },
    success: {
      main: '#2E7D32',
    },
    lightButton: createColor('rgba(2, 136, 209, 0.5)'),
  },
  components: {
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
      variants: [
        {
          props: { variant: 'secondary' },
          style: {
            textTransform: 'none',
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: '16px',
          },
        },
      ],
    },
  },
});

/**
 * The global shape defaults, layered onto {@link baseTheme}.
 *
 * Not `shape.borderRadius`, which is also the multiplier behind every `sx`
 * `borderRadius: n` — raising it would silently move all 86 existing call sites.
 */
export const theme = createTheme(baseTheme, {
  components: {
    MuiPaper: {
      styleOverrides: {
        // Not `root` — `square` Papers (the pinned mobile action bar, app chrome) opt out
        // by dropping this class, and should stay square.
        rounded: { borderRadius: SURFACE_RADIUS },
      },
    },
    // Transient surfaces stay tighter than panels, overriding the MuiPaper default above.
    MuiMenu: {
      styleOverrides: { paper: { borderRadius: TRANSIENT_SURFACE_RADIUS } },
    },
    MuiPopover: {
      styleOverrides: { paper: { borderRadius: TRANSIENT_SURFACE_RADIUS } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: TRANSIENT_SURFACE_RADIUS } },
    },
    MuiOutlinedInput: {
      // The notch's fieldset inherits this, so the outline follows the corner.
      styleOverrides: { root: { borderRadius: SURFACE_RADIUS } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: SURFACE_RADIUS } },
    },
    MuiToggleButton: {
      // Standalone toggles. Grouped ones are reshaped by `MuiToggleButtonGroup`.
      styleOverrides: { root: { borderRadius: SURFACE_RADIUS } },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { borderRadius: '3px 3px 0 0' },
      },
    },
  },
});

export const selectedColor: (
  theme: Theme,
) => ResponsiveStyleValue<AllSystemCSSProperties['backgroundColor']> = (theme) =>
  alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity);
